import { create } from "zustand";
import {
  buildWindows,
  loadDeviceEvents,
  processInBatches,
  DEVICE_CONCURRENCY,
  type RawTrackingEvent,
} from "../../pages/reports/utils/trackingHistoryWindows";

export const REPORT_EVENT_COLORS = [
  "#2563EB",
  "#DB2777",
  "#16A34A",
  "#D97706",
  "#7C3AED",
  "#0891B2",
  "#DC2626",
  "#65A30D",
];

export interface ReportEvent {
  ts: number;
  lat: number;
  lng: number;
  eventCode: number | null;
  eventType: string | null;
  geofenceId: string | null;
}

export interface ReportEventGroup {
  deviceId: string;
  vehicleName: string;
  color: string;
  events: ReportEvent[];
  total: number;
  loading: boolean;
  error: string | null;
}

export interface ReportEventsDevice {
  deviceId: string;
  vehicleName: string;
}

interface ReportEventsState {
  groups: Record<string, ReportEventGroup>;
  order: string[];
  loading: boolean;
  addresses: Record<string, string>;
  from: string | null;
  to: string | null;
  tz: string | null;
}

interface ReportEventsActions {
  loadRange: (
    devices: ReportEventsDevice[],
    from: string,
    to: string,
    opts?: { tz?: string; address?: boolean },
  ) => Promise<void>;
  clear: () => void;
}

type ReportEventsStore = ReportEventsState & ReportEventsActions;

const emptyState: ReportEventsState = {
  groups: {},
  order: [],
  loading: false,
  addresses: {},
  from: null,
  to: null,
  tz: null,
};

const mapEvent = (raw: RawTrackingEvent): ReportEvent => ({
  ts: raw.ts,
  lat: raw.lat,
  lng: raw.lng,
  eventCode: raw.event_code ?? null,
  eventType: raw.event_type ?? null,
  geofenceId: raw.geofence_id ?? null,
});

export const useReportEventsStore = create<ReportEventsStore>()((set) => ({
  ...emptyState,

  loadRange: async (devices, from, to, opts) => {
    const resolvedTz = opts?.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const withAddress = opts?.address ?? false;
    const order = devices.map((d) => d.deviceId);

    const windows = buildWindows(new Date(from).getTime(), new Date(to).getTime());

    const initial: Record<string, ReportEventGroup> = {};
    devices.forEach((d, i) => {
      initial[d.deviceId] = {
        deviceId: d.deviceId,
        vehicleName: d.vehicleName,
        color: REPORT_EVENT_COLORS[i % REPORT_EVENT_COLORS.length],
        events: [],
        total: 0,
        loading: true,
        error: null,
      };
    });

    set({ groups: initial, order, loading: true, addresses: {}, from, to, tz: resolvedTz });

    const allAddresses: Record<string, string> = {};

    await processInBatches(devices, DEVICE_CONCURRENCY, async (d) => {
      const { events: rawEvents, addresses, failedChunks, errorDetails } = await loadDeviceEvents(
        d.deviceId,
        windows,
        resolvedTz,
        withAddress,
      );

      Object.assign(allAddresses, addresses);

      const events = rawEvents
        .map(mapEvent)
        .filter((e) => e.lat != null && e.lng != null);

      set((state) => {
        const current = state.groups[d.deviceId];
        if (!current) return state;
        return {
          groups: {
            ...state.groups,
            [d.deviceId]: {
              ...current,
              events,
              total: events.length,
              loading: false,
              error:
                failedChunks > 0
                  ? `${failedChunks} tramo(s) con error — ${errorDetails[0] ?? "sin detalle"}`
                  : null,
            },
          },
        };
      });
    });

    set({ loading: false, addresses: allAddresses });
  },

  clear: () => set({ ...emptyState }),
}));
